import type { IResponse } from '@/features/api/extranet'
import { ensureSuccessStatusCode, useFetch } from '@/features/fetch'
import type { IDemandeService } from './IDemandeService'
import type { DemandeDTO } from '@/features/api/extranet/dto/Demande/shared'
import type { Dispositif } from '@/features/domaine/types/opco'
import demandeServices from './DemandeAllService'

export type BuildDemandeOptions<TDemande extends DemandeDTO> = {
  dispositif: Dispositif
  normaliserData?: (demande: TDemande) => TDemande
}

const INIT_DEFAULT: RequestInit = { sablier: true, dialogMode: 'retry' } as const
const INIT_DEFAULT_POST: RequestInit = { ...INIT_DEFAULT, reloadPageOnConcurrencyException: true } as const

export function buildDemandeService<TDemande extends DemandeDTO, TLov extends object>(
  options: BuildDemandeOptions<TDemande>,
) {
  const pathDispositif = options.dispositif.toLowerCase()

  async function loadDemande(id: number, init?: RequestInit) {
    if (!init) init = INIT_DEFAULT

    // Charge la demande
    const fetch = useFetch('extranet')
    const response = await fetch(`/demandes/${pathDispositif}/${id}`, init)
    ensureSuccessStatusCode(response)
    const result = (await response.json()) as IResponse<TDemande>

    // Todo: normaliser data au besoin
    return options.normaliserData?.call(null, result.data) ?? result.data
  }

  async function saveDemande(data: TDemande, init?: RequestInit) {
    if (data.dispositif !== options.dispositif)
      throw Error(`La demande ne correspond pas au disposif attendu (${options.dispositif}).`)

    if (!init) init = INIT_DEFAULT_POST

    const request: RequestInit = {
      ...init,
      method: 'PUT',
      body: JSON.stringify(data),
    }

    // Stocke la demande
    const fetch = useFetch('extranet')
    const response = await fetch(`/demandes/${pathDispositif}/${data.id}`, request)
    ensureSuccessStatusCode(response)
    const result = (await response.json()) as IResponse<TDemande>

    return result.data
  }

  function loadLov(init?: RequestInit) {
    return demandeServices.loadLovDispositif<TLov>(options.dispositif, init)
  }

  async function downloadCerfa(id: number, init?: RequestInit): Promise<Blob> {
    if (!init) init = { sablier: true, dialogMode: 'retry' }

    // Charge la demande
    const fetch = useFetch('extranet')
    const response = await fetch(`/demandes/${pathDispositif}/${id}/cerfa`, init)
    ensureSuccessStatusCode(response)

    return await response.blob()
  }

  return {
    loadDemande,
    saveDemande,
    loadLov,
    downloadCerfa,
  } as IDemandeService<TDemande, TLov>
}
